import $ = require("jquery");
import _ = require("underscore");
import proj4 = require("proj4");
import ol = require("ol");
import Utils = require("../utils");
import Portal = require("portal-gis");

const parser = new ol.format.WMSCapabilities();

const isPointFields = ["xCoordinate", "yCoordinate"];

const DOTS_PER_INCH = 90;
const INCHES_PER_UNIT = {
  inches: 1.0,
  ft: 12.0,
  mi: 63360.0,
  m: 39.37,
  km: 39370,
  dd: 4374754,
  yd: 36,
};

function isPoint(item) {
  if (isPointFields.some((f) => typeof item[f] === "undefined")) return false;
  if (isPointFields.every((f) => item[f] === 0)) return false;
  return true;
}

interface Point {
  x: number;
  y: number;
}

class Transformer {
  transform: {
    forward: (p: Point) => Point;
    inverse: (p: Point) => Point;
  };

  constructor(sourceSrs: string | number, targetSrs: string | number) {
    if (typeof sourceSrs == "number") sourceSrs = `EPSG:${sourceSrs}`;
    if (typeof targetSrs == "number") targetSrs = `EPSG:${targetSrs}`;
    this.transform = proj4(sourceSrs, targetSrs);
  }

  point(p: number[]) {
    let result = this.transform.forward({ x: p[0], y: p[1] });
    return [result.x, result.y];
  }

  points(p: number[][]) {
    return p.map((p) => this.point(p));
  }

  paths(p: number[][][]) {
    return p.map((p) => this.points(p));
  }
}

class Geo {
  static about = `Spatial utility functions`;

  private asCode(dst: string | Rhythm.GIS.SpatialReference) {
    if (typeof dst === "string") return dst;
    else {
      if (typeof dst.wkid === "string") return dst.wkid;
      if (typeof dst.wkid === "number") return `EPSG:${dst.wkid}`;
      return dst.wkt;
    }
  }

  private pair(points: Array<number>) {
    var result = <Array<Array<number>>>[];
    for (var i = 0; i < points.length; i += 2) {
      result.push([points[i + 0], points[i + 1]]);
    }
    return result;
  }

  private unpair(points: Array<Array<number>>) {
    var result = <Array<number>>[];
    points.forEach((p) => result.push(p[0], p[1]));
    return result;
  }

  fixExtent(extent: number[]) {
    let fixed = false;
    let i = 0;
    if (extent[0 + i] > extent[2 + i]) {
      fixed = true;
      let swap = extent[0 + i];
      extent[0 + i] = extent[2 + i];
      extent[2 + i] = swap;
    }
    i += 1;
    if (extent[0 + i] > extent[2 + i]) {
      fixed = true;
      let swap = extent[0 + i];
      extent[0 + i] = extent[2 + i];
      extent[2 + i] = swap;
    }
    return fixed;
  }

  transformExtent(dst: string | Rhythm.GIS.SpatialReference, geom: Rhythm.GIS.Spatial<Rhythm.GIS.Extent>) {
    var dstSrs = this.asCode(dst);
    var srcSrs = this.asCode(geom.spatialReference);
    var extent = this.unpair(this.transform(srcSrs, dstSrs, this.pair(geom.geometry)));

    return <Rhythm.GIS.Spatial<Rhythm.GIS.Extent>>{
      geometry: extent,
      spatialReference: {
        wkid: dstSrs,
      },
    };
  }

  pixelToExtent(map: ol.Map, pixel: ol.Pixel, bufferSize = 8) {
    let wn = map.getCoordinateFromPixel(pixel.map((v) => v - bufferSize));
    let es = map.getCoordinateFromPixel(pixel.map((v) => v + bufferSize));
    let extent = [wn[0], es[1], es[0], wn[1]]; // west, south, east, north
    geo.fixExtent(extent);
    let wkid = map.getView().getProjection().getCode();
    let rhythmExt = geo.transformExtent("EPSG:4326", {
      geometry: extent,
      spatialReference: {
        wkid: wkid,
      },
    });
    return rhythmExt.geometry;
  }

  transformer(src, dst) {
    return new Transformer(src, dst);
  }

  transform(src, dst, points: Rhythm.GIS.Point[]) {
    var transformer = this.transformer(src, dst);
    return transformer.points(points);
  }

  grow(extent: number[], coordinates: number[]) {
    if (_.isArray(coordinates[0])) {
      coordinates.forEach((p) => (extent = this.grow(extent, <any>p)));
    } else {
      let newExtent = [coordinates[0], coordinates[1], coordinates[0], coordinates[1]];
      extent = extent ? ol.extent.extend(extent, newExtent) : newExtent;
    }
    return extent;
  }

  asExtent(extent: string) {
    return {
      geometry: extent ? Utils.split(extent).map(parseFloat) : null,
      spatialReference: { wkid: "EPSG:4326" },
    };
  }

  isPoint(item: { xCoordinate: any; yCoordinate: any }) {
    return isPoint(item);
  }

  beforeProcess(
    item: { data: any },
    locatingProperties = "address,property,parcel",
    fields = ["xCoordinate", "yCoordinate"]
  ) {
    if (item.data["Spatial"]) {
      let src = item.data["Spatial"];
      if (this.isPoint(src)) {
        // console.log("ignore locating properties, we already have x/y on `Spatial`");
        fields.forEach((key) => (item.data[key] = parseFloat(src[key])));
        return item;
      }
    }

    let paths = Utils.split(locatingProperties);
    paths.some((path) => {
      let src = Utils.data(item.data, path);
      if (!src) return false;

      if (src["Spatial"]) {
        // console.log("when usegis=true Spatial contains the actual x/y, so prefer these values");
        src = src["Spatial"];
      }

      // tradelicense has serviceRequests[]
      if ($.isArray(src)) {
        return (<Array<any>>src).some((innerSrc) => {
          if (innerSrc["Spatial"]) {
            // console.log("when usegis=true Spatial contains the actual x/y, so prefer these values");
            innerSrc = innerSrc["Spatial"];
          }
          if (this.isPoint(innerSrc)) {
            fields.forEach((key) => (item.data[key] = parseFloat(innerSrc[key])));
            return true;
          }
        });
      }

      // assume it is an immediate reference (e.g. address)
      if (!this.isPoint(src)) return false;
      fields.forEach((key) => (item.data[key] = parseFloat(src[key])));
      return true;
    });
  }

  getOl3FeatureData(feature: ol.Feature) {
    let data = feature.get("attributes");
    if (!data) {
      // this feature was created from the wfs reader or another outside source
      // copies all properties except geometry
      let props = feature.getProperties();
      let keys = _.keys(props).filter((k) => typeof props[k] !== "object");
      data = {};
      keys.forEach((key) => (data[key] = props[key]));
    }
    return data;
  }

  getFeatureData(feature: Rhythm.GIS.FeatureQueryResultFeature) {
    let data = null;
    if (!data) {
      data = feature.attributes;
    }
    if (!data) {
      data = Utils.extend(feature);
      if (data) data = data.config;
      if (data) data = data.Attributes;
    }
    if (!data) {
      data = feature;
    }
    return data;
  }

  // geo

  // from OpenLayers.Util.normalizeScale
  normalizeScale(scale) {
    return scale > 1.0 ? 1.0 / scale : scale;
  }

  // from OpenLayers.Util.getResolutionFromScale
  getResolutionFromScale(scale, units) {
    var resolution;
    if (scale) {
      if (units == null) {
        units = "degrees";
      }
      var normScale = this.normalizeScale(scale);
      resolution = 1 / (normScale * INCHES_PER_UNIT[units] * DOTS_PER_INCH);
    }
    return resolution;
  }

  // ogc
  getCaps(layer: Portal.MapLayerLeaf) {
    var d = $.Deferred<ogc.wms.Wms>();
    var req = $.ajax({ url: layer.url, proxy: true });

    req.then((response) => {
      var result = <ogc.wms.Wms>(<any>parser.read(response));
      d.resolve(result);
    });

    req.fail((err) => {
      console.error("getcaps: ", err);
      d.reject(err);
    });

    return d;
  }
}

let geo = new Geo();
export = geo;
